import { Link, createFileRoute, useRouterState } from '@tanstack/react-router'
import { MODEL_REGISTRY_MODULE_ID } from '@/features/model-registry'
import { TrainModelPage } from '@/features/model-training'
import { can, useSession } from '@/shared/auth'
import { useModules } from '@/shared/modules'

// `datasets_`: under the Datasets address without being rendered inside the Datasets page.
export const Route = createFileRoute('/app/model-training/datasets_/$datasetId/train')({
  component: TrainModelRoute,
})

function TrainModelRoute() {
  const { datasetId } = Route.useParams()
  const navigate = Route.useNavigate()
  // Handed on by the Preprocessing; absent after a reload or when the address was opened directly.
  const listSearch = useRouterState({ select: (state) => state.location.state.datasetList }) ?? {}
  // The registry is a module of its own: not every deployment serves it.
  const session = useSession()
  const registry =
    useModules().some((module) => module.id === MODEL_REGISTRY_MODULE_ID) &&
    can(session, 'model-registry:view')

  return (
    <TrainModelPage
      datasetId={datasetId}
      onPrevious={() => {
        void navigate({
          to: '/app/model-training/datasets/$datasetId/preprocessing',
          params: { datasetId },
          state: { datasetList: listSearch },
        })
      }}
      onOpenFeatures={() => {
        void navigate({
          to: '/app/model-training/datasets/$datasetId/features',
          params: { datasetId },
          state: { datasetList: listSearch },
        })
      }}
      onBackToDatasets={() => {
        void navigate({ to: '/app/model-training/datasets', search: listSearch })
      }}
      renderDatasetsLink={(content, className) => (
        <Link to="/app/model-training/datasets" search={listSearch} className={className}>
          {content}
        </Link>
      )}
      renderPreviewLink={(content, className) => (
        <Link
          to="/app/model-training/datasets/$datasetId/preview"
          params={{ datasetId }}
          state={{ datasetList: listSearch }}
          className={className}
        >
          {content}
        </Link>
      )}
      renderFeaturesLink={(content, className) => (
        <Link
          to="/app/model-training/datasets/$datasetId/features"
          params={{ datasetId }}
          state={{ datasetList: listSearch }}
          className={className}
        >
          {content}
        </Link>
      )}
      renderRegistryLink={
        registry
          ? (content, className) => (
              // Default list state: the new model is listed first (newest first).
              <Link to="/app/model-registry/models" className={className}>
                {content}
              </Link>
            )
          : undefined
      }
      renderPreprocessingLink={(content, className) => (
        <Link
          to="/app/model-training/datasets/$datasetId/preprocessing"
          params={{ datasetId }}
          state={{ datasetList: listSearch }}
          className={className}
        >
          {content}
        </Link>
      )}
    />
  )
}
